import React, { useState } from 'react'
import { autoAligner, type AlignmentPair, type AlignmentResult } from '@/lib/cat/autoAligner'
import { Upload, AlertCircle, CheckCircle, X, ArrowRight, Save, Trash2, RefreshCw } from 'lucide-react'
import Button from '@/components/common/Button'

interface AutoAlignerDialogProps {
  isOpen: boolean
  onClose: () => void
  sourceLang: string
  targetLang: string
  userId: string
  projectId: string
}

const AutoAlignerDialog: React.FC<AutoAlignerDialogProps> = ({
  isOpen,
  onClose,
  sourceLang,
  targetLang,
  userId,
  projectId
}) => {
  const [step, setStep] = useState<'upload' | 'align' | 'review'>('upload')
  const [sourceText, setSourceText] = useState('')
  const [targetText, setTargetText] = useState('')
  const [alignmentResult, setAlignmentResult] = useState<AlignmentResult | null>(null)
  const [alignmentPairs, setAlignmentPairs] = useState<AlignmentPair[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [minConfidence, setMinConfidence] = useState(60)
  const [strategy, setStrategy] = useState<'one-to-one' | 'length-based'>('one-to-one')

  const handleFileUpload = async (file: File, type: 'source' | 'target') => {
    const text = await file.text()
    if (type === 'source') {
      setSourceText(text)
    } else {
      setTargetText(text)
    }
  }

  const handleAlign = async () => {
    if (!sourceText || !targetText) {
      alert('Please upload both source and target documents.')
      return
    }

    setLoading(true)
    try {
      // Segment texts into arrays (split by sentences/paragraphs)
      const sourceSegments = sourceText.split(/\n+/).filter(s => s.trim().length > 0)
      const targetSegments = targetText.split(/\n+/).filter(s => s.trim().length > 0)
      
      const result = await autoAligner.align({
        sourceSegments,
        targetSegments,
        sourceLanguage: sourceLang,
        targetLanguage: targetLang
      })
      
      setAlignmentResult(result)
      setAlignmentPairs(result.pairs)
      setStep('review')
    } catch (error) {
      console.error('Alignment failed:', error)
      alert('Alignment failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveToTM = async () => {
    if (!alignmentPairs || alignmentPairs.length === 0) {
      alert('No alignment pairs to save.')
      return
    }

    // Filter by confidence
    const filteredPairs = autoAligner.filterByConfidence(alignmentPairs, minConfidence)

    if (filteredPairs.length === 0) {
      alert(`No pairs meet the ${minConfidence}% confidence threshold.`)
      return
    }

    const confirmed = confirm(
      `Save ${filteredPairs.length} pairs to Translation Memory?\n\n` +
      `Minimum confidence: ${minConfidence}%\n` +
      `This will add these translations to your TM for future reuse.`
    )

    if (!confirmed) return

    setSaving(true)
    try {
      const result = await autoAligner.saveToTM(
        alignmentPairs,
        userId,
        sourceLang,
        targetLang,
        minConfidence
      )

      alert(`✅ Successfully saved ${result.saved} pairs to Translation Memory!` + 
            (result.errors > 0 ? `\n⚠️ ${result.errors} failed to save.` : ''))
      onClose()
      resetState()
    } catch (error) {
      console.error('Save failed:', error)
      alert('Failed to save to TM. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleRemovePair = (index: number) => {
    setAlignmentPairs(prev => prev.filter((_, i) => i !== index))
  }

  const resetState = () => {
    setStep('upload')
    setSourceText('')
    setTargetText('')
    setAlignmentResult(null)
    setAlignmentPairs([])
    setMinConfidence(60)
  }

  const stats = alignmentResult ? autoAligner.getStatistics(alignmentResult) : null
  const filteredCount = alignmentPairs ? autoAligner.filterByConfidence(alignmentPairs, minConfidence).length : 0

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Auto-Aligner</h2>
            <p className="text-sm text-gray-700 mt-1">
              Align source and target documents to build Translation Memory
            </p>
          </div>
          <button
            onClick={() => {
              onClose()
              resetState()
            }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-700" />
          </button>
        </div>

        {/* Steps Indicator */}
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-center space-x-8">
            <div className={`flex items-center space-x-2 ${step === 'upload' ? 'text-blue-600' : 'text-gray-800'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === 'upload' ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}>1</div>
              <span className="font-medium">Upload</span>
            </div>
            <ArrowRight className="h-5 w-5 text-gray-800" />
            <div className={`flex items-center space-x-2 ${step === 'align' ? 'text-blue-600' : 'text-gray-800'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === 'align' ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}>2</div>
              <span className="font-medium">Align</span>
            </div>
            <ArrowRight className="h-5 w-5 text-gray-800" />
            <div className={`flex items-center space-x-2 ${step === 'review' ? 'text-blue-600' : 'text-gray-800'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === 'review' ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}>3</div>
              <span className="font-medium">Review & Save</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-900">
                    <p className="font-medium mb-1">How it works:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Upload your source document ({sourceLang.toUpperCase()})</li>
                      <li>Upload your target translation ({targetLang.toUpperCase()})</li>
                      <li>Choose alignment strategy</li>
                      <li>Review and save to Translation Memory</li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* Source Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Source Document ({sourceLang.toUpperCase()})
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                    <input
                      type="file"
                      accept=".txt,.docx,.pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) handleFileUpload(file, 'source')
                      }}
                      className="hidden"
                      id="source-upload"
                    />
                    <label htmlFor="source-upload" className="cursor-pointer">
                      <Upload className="h-12 w-12 text-gray-800 mx-auto mb-3" />
                      <p className="text-sm text-gray-800">
                        {sourceText ? (
                          <span className="text-green-600 font-medium">
                            ✓ Uploaded ({sourceText.length} chars)
                          </span>
                        ) : (
                          'Click to upload or paste text'
                        )}
                      </p>
                    </label>
                  </div>
                  <textarea
                    value={sourceText}
                    onChange={(e) => setSourceText(e.target.value)}
                    placeholder="Or paste source text here..."
                    className="mt-3 w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                {/* Target Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Target Document ({targetLang.toUpperCase()})
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                    <input
                      type="file"
                      accept=".txt,.docx,.pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) handleFileUpload(file, 'target')
                      }}
                      className="hidden"
                      id="target-upload"
                    />
                    <label htmlFor="target-upload" className="cursor-pointer">
                      <Upload className="h-12 w-12 text-gray-800 mx-auto mb-3" />
                      <p className="text-sm text-gray-800">
                        {targetText ? (
                          <span className="text-green-600 font-medium">
                            ✓ Uploaded ({targetText.length} chars)
                          </span>
                        ) : (
                          'Click to upload or paste text'
                        )}
                      </p>
                    </label>
                  </div>
                  <textarea
                    value={targetText}
                    onChange={(e) => setTargetText(e.target.value)}
                    placeholder="Or paste target text here..."
                    className="mt-3 w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              {/* Strategy Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Alignment Strategy
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setStrategy('one-to-one')}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      strategy === 'one-to-one'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <h4 className="font-semibold text-gray-900 mb-1">One-to-One</h4>
                    <p className="text-sm text-gray-800">
                      Best when documents have the same number of segments
                    </p>
                  </button>
                  <button
                    onClick={() => setStrategy('length-based')}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      strategy === 'length-based'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <h4 className="font-semibold text-gray-900 mb-1">Length-Based</h4>
                    <p className="text-sm text-gray-800">
                      Matches segments by similar character count
                    </p>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Align (Loading) */}
          {step === 'align' && (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Aligning documents...</h3>
                <p className="text-gray-800">This may take a moment</p>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 'review' && alignmentResult && stats && (
            <div className="space-y-6">
              {/* Statistics */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-600">{stats.totalPairs}</div>
                  <div className="text-sm text-green-700">Total Pairs</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-600">{stats.avgConfidence}%</div>
                  <div className="text-sm text-blue-700">Avg Confidence</div>
                </div>
                <div className="bg-gold-50 border border-gold-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-gold-600">{stats.unalignedSource}</div>
                  <div className="text-sm text-gold-700">Unaligned Source</div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-purple-600">{stats.unalignedTarget}</div>
                  <div className="text-sm text-purple-700">Unaligned Target</div>
                </div>
              </div>

              {/* Confidence Filter */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-900">
                    Minimum Confidence Threshold
                  </label>
                  <span className="text-lg font-bold text-blue-600">{minConfidence}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={minConfidence}
                  onChange={(e) => setMinConfidence(Number(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-700 mt-1">
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
                <p className="text-sm text-gray-800 mt-2">
                  {filteredCount} pairs will be saved (≥ {minConfidence}% confidence)
                </p>
              </div>

              {/* Pairs List */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <h4 className="font-medium text-gray-900">Alignment Pairs</h4>
                </div>
                <div className="max-h-96 overflow-y-auto divide-y divide-gray-200">
                  {alignmentPairs.map((pair, index) => (
                    <div
                      key={index}
                      className={`p-4 hover:bg-gray-50 ${
                        pair.confidence < minConfidence ? 'opacity-50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <span className="text-sm font-medium text-gray-700">#{index + 1}</span>
                          <div className={`px-2 py-1 rounded text-xs font-medium ${
                            pair.confidence >= 80
                              ? 'bg-green-100 text-green-700'
                              : pair.confidence >= 60
                              ? 'bg-gold-100 text-gold-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {pair.confidence}% confidence
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemovePair(index)}
                          className="p-1 hover:bg-red-50 rounded text-red-600"
                          title="Remove pair"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-xs text-gray-700 mb-1">Source</div>
                          <p className="text-gray-900">{pair.sourceText}</p>
                        </div>
                        <div>
                          <div className="text-xs text-gray-700 mb-1">Target</div>
                          <p className="text-gray-900">{pair.targetText}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            {step === 'upload' && (
              <>
                <div className="text-sm text-gray-800">
                  Upload both documents to continue
                </div>
                <div className="flex items-center space-x-3">
                  <Button variant="secondary" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    leftIcon={<RefreshCw className="h-4 w-4" />}
                    onClick={handleAlign}
                    disabled={!sourceText || !targetText || loading}
                  >
                    {loading ? 'Aligning...' : 'Start Alignment'}
                  </Button>
                </div>
              </>
            )}
            {step === 'review' && (
              <>
                <Button
                  variant="secondary"
                  leftIcon={<ArrowRight className="h-4 w-4 rotate-180" />}
                  onClick={() => setStep('upload')}
                >
                  Back to Upload
                </Button>
                <div className="flex items-center space-x-3">
                  <Button variant="secondary" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    leftIcon={<Save className="h-4 w-4" />}
                    onClick={handleSaveToTM}
                    disabled={saving || filteredCount === 0}
                  >
                    {saving ? 'Saving...' : `Save ${filteredCount} Pairs to TM`}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AutoAlignerDialog